import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MoovyButton, MoovyDialog, MoovyProgress, MoovyToast, } from 'moovy-ui-core';
import { parseMutationError } from 'moovy-ui-core/react-query-utils';
import { useApplication } from '../../apis/applications/useApplication.js';
import { useApproveApplication } from '../../apis/applications/useApproveApplication.js';
import { useRejectApplication } from '../../apis/applications/useRejectApplication.js';
import { ROUTES } from '../../app/routes.js';
import ApplicationInfoCard from './components/applicationInfoCard.js';
import ApplicationStatusChip from './components/applicationStatusChip.js';
import BillingInfoCard from './components/billingInfoCard.js';
import ContactPersonCard from './components/contactPersonCard.js';
import ProductCard from './components/productCard.js';
import ValidationSummary from './components/validationSummary.js';
const APPROVE_INLINE_CODES = new Set([
    'ORGANIZATION_APPROVAL_BLOCKED',
    'AGREEMENT_INVALID_STATE',
]);
const REJECT_INLINE_CODES = new Set(['AGREEMENT_INVALID_STATE']);
const ApplicationDetailPage = () => {
    const { ref } = useParams();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { data: application, isLoading, error } = useApplication(ref !== null && ref !== void 0 ? ref : '');
    const approveMutation = useApproveApplication();
    const rejectMutation = useRejectApplication();
    const [approveDialogOpen, setApproveDialogOpen] = useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectAttempted, setRejectAttempted] = useState(false);
    const formatDate = (isoDate) => {
        const dt = DateTime.fromISO(isoDate);
        if (!dt.isValid)
            return isoDate;
        return i18n.language === 'fi'
            ? dt.toFormat('dd.MM.yyyy HH:mm')
            : dt.toFormat('MM/dd/yyyy HH:mm');
    };
    const approveError = parseMutationError(approveMutation);
    const rejectError = parseMutationError(rejectMutation);
    const approveInlineMessage = (approveError === null || approveError === void 0 ? void 0 : approveError.code) && APPROVE_INLINE_CODES.has(approveError.code)
        ? t(`applicationDetail.approveError.${approveError.code}`)
        : null;
    const rejectInlineMessage = (rejectError === null || rejectError === void 0 ? void 0 : rejectError.code) && REJECT_INLINE_CODES.has(rejectError.code)
        ? t(`applicationDetail.rejectError.${rejectError.code}`)
        : null;
    const handleApprove = () => {
        if (!ref)
            return;
        approveMutation.mutate(ref, {
            onSuccess: () => {
                setApproveDialogOpen(false);
                MoovyToast.success(t('applicationDetail.approveSuccess'));
                navigate(ROUTES.APPLICATIONS);
            },
            onError: (err) => {
                const parsed = parseMutationError({
                    data: undefined,
                    error: err,
                    isPending: false,
                });
                const code = parsed === null || parsed === void 0 ? void 0 : parsed.code;
                if (!code || !APPROVE_INLINE_CODES.has(code)) {
                    setApproveDialogOpen(false);
                    MoovyToast.error(t('applicationDetail.approveErrorGeneric'));
                }
            },
        });
    };
    const handleReject = () => {
        setRejectAttempted(true);
        if (!ref || !rejectReason.trim())
            return;
        rejectMutation.mutate({ ref, reason: rejectReason.trim() }, {
            onSuccess: () => {
                setRejectDialogOpen(false);
                setRejectReason('');
                setRejectAttempted(false);
                MoovyToast.success(t('applicationDetail.rejectSuccess'));
                navigate(ROUTES.APPLICATIONS);
            },
            onError: (err) => {
                const parsed = parseMutationError({
                    data: undefined,
                    error: err,
                    isPending: false,
                });
                const code = parsed === null || parsed === void 0 ? void 0 : parsed.code;
                if (!code || !REJECT_INLINE_CODES.has(code)) {
                    setRejectDialogOpen(false);
                    MoovyToast.error(t('applicationDetail.rejectErrorGeneric'));
                }
            },
        });
    };
    if (isLoading) {
        return (_jsx(Box, { sx: {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 300,
            }, children: _jsx(MoovyProgress, {}) }));
    }
    if (error || !application) {
        const errorCode = error && typeof error === 'object' && 'code' in error
            ? error.code
            : undefined;
        const isNotFound = errorCode === 'AGREEMENT_NOT_FOUND' || !error;
        const headingKey = isNotFound
            ? 'applicationDetail.notFound.title'
            : 'common.error';
        const descriptionKey = isNotFound
            ? 'applicationDetail.notFound.description'
            : 'common.errorDescription';
        return (_jsxs(Box, { children: [_jsxs(Link, { to: ROUTES.APPLICATIONS, style: {
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                    }, children: [_jsx(ArrowBackRounded, { fontSize: "small", sx: { color: 'text.primary' } }), _jsx(Typography, { variant: "body2", color: "text.primary", sx: { fontWeight: 600 }, children: t('applicationDetail.backToList') })] }), _jsx(Typography, { variant: "h5", sx: { fontWeight: 700, mt: 2 }, children: t(headingKey) }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mt: 1 }, children: t(descriptionKey) })] }));
    }
    const isPending = application.status === 'APPLIED';
    const companyName = application.organization.companyName;
    return (_jsxs(Box, { sx: { minWidth: 0 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, mb: 2 }, children: [_jsx(Link, { to: ROUTES.APPLICATIONS, style: { textDecoration: 'none', color: 'inherit' }, children: _jsx(Typography, { variant: "body2", color: "text.secondary", children: t('nav.applications') }) }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: ">" }), _jsx(Typography, { variant: "body2", children: companyName })] }), _jsxs(Box, { sx: {
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'flex-start' },
                    gap: 2,
                    mb: 3,
                }, children: [_jsxs(Box, { children: [_jsxs(Box, { sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    mb: 1,
                                    flexWrap: 'wrap',
                                }, children: [_jsx(Typography, { variant: "h4", sx: { fontWeight: 700, wordBreak: 'break-word' }, children: companyName }), _jsx(ApplicationStatusChip, { status: application.status })] }), _jsxs(Typography, { variant: "body2", sx: { color: 'grey.700' }, children: [t('applicationDetail.submitted'), ":", ' ', formatDate(application.submittedAt)] })] }), isPending && (_jsxs(Box, { sx: { display: 'flex', gap: 1, flexShrink: 0 }, children: [_jsx(MoovyButton, { variant: "outlined", color: "primary", onClick: () => setRejectDialogOpen(true), children: t('applicationDetail.reject') }), _jsx(MoovyButton, { variant: "contained", color: "primary", onClick: () => setApproveDialogOpen(true), children: t('applicationDetail.approve') })] }))] }), _jsx(Box, { sx: { mb: 3 }, children: _jsx(ValidationSummary, { checks: application.validationChecks }) }), _jsxs(Grid, { container: true, spacing: 3, children: [_jsx(Grid, { size: { xs: 12, md: 6 }, children: _jsx(ApplicationInfoCard, { organization: application.organization }) }), _jsx(Grid, { size: { xs: 12, md: 6 }, children: _jsx(ProductCard, { product: application.product, billingChannel: application.billingChannel, line: application.line }) }), _jsx(Grid, { size: { xs: 12, md: 6 }, children: _jsx(ContactPersonCard, { contactPerson: application.contactPerson, strongAuthCompleted: application.validationChecks.strongAuthCompleted }) }), _jsx(Grid, { size: { xs: 12, md: 6 }, children: _jsx(BillingInfoCard, { billingInfo: application.billingInfo }) })] }), _jsx(MoovyDialog, { open: approveDialogOpen, title: t('applicationDetail.approveDialogTitle'), loading: approveMutation.isPending, fullScreen: false, maxWidth: "sm", fullWidth: true, PaperProps: { sx: { borderRadius: '10px' } }, onClose: () => {
                    setApproveDialogOpen(false);
                    approveMutation.reset();
                }, onSubmit: handleApprove, submitButtonContent: t('applicationDetail.approve'), cancelButtonText: t('applicationDetail.cancel'), children: _jsxs(Box, { children: [_jsx(Typography, { variant: "body2", children: t('applicationDetail.approveDialogContent', {
                                company: companyName,
                            }) }), approveInlineMessage && (_jsx(Alert, { severity: "error", sx: { mt: 2 }, role: "alert", children: approveInlineMessage }))] }) }), _jsx(MoovyDialog, { open: rejectDialogOpen, title: t('applicationDetail.rejectDialogTitle'), loading: rejectMutation.isPending, fullScreen: false, maxWidth: "sm", fullWidth: true, PaperProps: { sx: { borderRadius: '10px' } }, onClose: () => {
                    setRejectDialogOpen(false);
                    setRejectReason('');
                    setRejectAttempted(false);
                    rejectMutation.reset();
                }, onSubmit: rejectReason.trim() === '' ? undefined : handleReject, submitButtonContent: rejectReason.trim() === '' ? undefined : t('applicationDetail.reject'), cancelButtonText: t('applicationDetail.cancel'), children: _jsxs(Box, { children: [_jsx(Typography, { variant: "body2", sx: { mb: 2 }, children: t('applicationDetail.rejectDialogContent', {
                                company: companyName,
                            }) }), _jsx(TextField, { label: t('applicationDetail.rejectionReason'), value: rejectReason, onChange: (e) => setRejectReason(e.target.value), multiline: true, rows: 3, fullWidth: true, required: true, error: rejectAttempted && rejectReason.trim() === '', helperText: rejectAttempted && rejectReason.trim() === ''
                                ? t('applicationDetail.reasonRequired')
                                : undefined }), rejectInlineMessage && (_jsx(Alert, { severity: "error", sx: { mt: 2 }, role: "alert", children: rejectInlineMessage }))] }) })] }));
};
export default ApplicationDetailPage;
